<template>
  <div
    :class="['text-input', { disabled, 'has-slot': hasDefaultSlot, 'has-symbol': symbol }]"
    :style="removeMargin ? { '--text-input-margin-bottom': 0 } : null"
  >
    <dropdown
      flex
      :align="align"
      :active="active"
      @close="active = false"
    >
      <div class="input-wrapper">
        <label v-if="label" :for="id">{{ label }}</label>

        <span v-if="symbol" class="symbol">
          {{ symbol }}
        </span>

        <input
          ref="input"
          v-model.trim="localValue"
          :id="id"
          :name="id"
          :type="type"
          :required="required"
          :placeholder="placeholder"
          :disabled="disabled"
          :maxlength="maxlength"
          :autocomplete="options ? 'off' : 'on'"
          @input="update"
          @focus="active = true"
        />

        <span v-if="loading" class="slot">
          <span class="spinner" />
        </span>

        <div v-else-if="hasDefaultSlot" class="slot">
          <slot />
        </div>
      </div>

      <template #menu>
        <dropdown-menu
          hide-no-results
          :options="filteredOptions"
          :activeValue="value"
          @select="handleSelect"
        />
      </template>
    </dropdown>
  </div>
</template>

<script>
import Dropdown from './Dropdown';
import DropdownMenu from './DropdownMenu';

export default {
  components: {
    Dropdown,
    DropdownMenu,
  },

  props: {
    value: [String, Number],
    id: String,
    label: String,
    placeholder: String,
    required: Boolean,
    disabled: Boolean,
    symbol: String,
    type: {
      type: String,
      default: 'text',
    },
    maxlength: {
      type: String,
      default: '300',
    },
    removeMargin: Boolean,
    align: {
      type: String,
      default: 'left',
    },
    options: Array,
    loading: Boolean,
  },

  data() {
    return {
      active: false,
      localValue: this.value,
    };
  },

  watch: {
    value(value) {
      this.localValue = value;
    },
  },

  computed: {
    hasDefaultSlot() {
      return !!this.$slots.default;
    },

    filteredOptions() {
      if (!this.options) return [];

      return this.options.filter(({ label }) => label.toLowerCase().indexOf(this.value?.toLowerCase()) > -1);
    },
  },

  methods: {
    focus() {
      this.$refs.input.focus();
    },

    update() {
      let value = this.localValue;

      if (value !== '') {
        if (this.type === 'decimal') {
          value = parseFloat(value.replace(/,/g, ''));

          if (Number.isNaN(value)) {
            value = 0;
          }
        } else if (this.type === 'number') {
          value = parseInt(value.replace(/,/g, ''), 10);

          if (Number.isNaN(value)) {
            value = 0;
          }
        }
      }

      this.$emit('input', value);
    },

    handleSelect(value) {
      this.$emit('input', value);
      this.active = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.text-input {
  margin-bottom: var(--text-input-margin-bottom, $gp);
  position: relative;
  width: 100%;

  &.disabled {
    opacity: 0.5;
  }

  &.has-slot {
    input {
      padding-right: $gp * 2;
    }
  }

  &.has-symbol {
    input {
      padding-left: $gp;
    }
  }

  .dropdown,
  .input-wrapper {
    width: 100%;
  }

  input {
    background-color: var(--theme-background);
    border: solid 1px var(--theme-input-border);
    border-radius: $border-radius;
    outline: 0;
    font-size: 16px;
    padding: $gp * .5 $gp * .75;
    padding-right: $gp * .75;
    width: 100%;
    height: 48px;

    &:focus {
      border-color: $color-green-1;
    }
  }

  label {
    color: $color-gray-5;
    background-color: var(--theme-background);
    font-size: 12px;
    position: absolute;
    transform-origin: top left;
    padding: 0 3px;
    transform: translate(13px, -9px);
    font-weight: bold;
  }
}

.slot,
.symbol {
  position: absolute;
  height: 48px;
  display: inline-flex;
  align-items: center;
}

.slot {
  right: 0;
  padding-right: $gp * .5;
}

.symbol {
  left: 0;
  padding-left: $gp * .5;
}

.spinner {
  display: block;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  transform: translateZ(0);
  animation: spin 1s infinite linear;
  border-top: 2px solid $color-green-1;
  border-right: 2px solid $color-green-1;
  border-bottom: 2px solid $color-green-1;
  border-left: 2px solid transparent;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
